/**
 * GroupProfilePictureUpload component.
 * Displays a profile picture with an overlay that lets group owners or admins
 * pick and upload a new image to Supabase.
 */

import { useRef, useState } from 'react';
import { supabase } from '../utils/supabase';
import '../styles/GroupProfilePictureUpload.css';

interface GroupProfilePictureUploadProps {
  currentUrl: string | null;
  groupId: string;
  onUploadComplete: (url: string) => void;
  size?: number;
  fallbackLetter?: string;
}

export default function GroupProfilePictureUpload({
  currentUrl,
  groupId,
  onUploadComplete,
  size = 120,
  fallbackLetter = 'G',
}: GroupProfilePictureUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const displayUrl = previewUrl || currentUrl;

  /**
   * Opens the file picker dialog.
   */
  const handleClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  /**
   * Handles file selection: validates, previews, and uploads.
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a PNG, JPEG, WebP, or GIF image.');
      return;
    }

    // Validate file size (max 5 MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5 MB.');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);

      const ext = file.name.split('.').pop();
      const filePath = `${groupId}/profile_picture.${ext}`;

      // Upload to 'groups' bucket
      const { data, error: uploadError } = await supabase.storage
        .from('groups')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage.from('groups').getPublicUrl(data.path);

      // Append timestamp to prevent showing caching of the old image
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      onUploadComplete(publicUrl);
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="ppu-field-container">
      <h3 className="ppu-header">Group Avatar</h3>

      {/* Profile picture upload centered below */}
      <div className="ppu-container-wrapper">
        <div
          className="ppu-container"
          onClick={handleClick}
          style={{ width: size, height: size }}
        >
          {displayUrl ? (
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={displayUrl}
              alt="Group Profile Picture"
              className="ppu-image"
            />
          ) : (
            <div className="ppu-default-avatar">
              {fallbackLetter.charAt(0).toUpperCase()}
            </div>
          )}

          {!isUploading && (
            <div className="ppu-overlay">
              <span className="ppu-overlay-icon">📷</span>
              <span className="ppu-overlay-text">Change</span>
            </div>
          )}

          {isUploading && (
            <div className="ppu-loading-overlay">
              <div className="ppu-spinner" />
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="ppu-file-input"
          onChange={handleFileChange}
        />

        {error && <div className="ppu-error">{error}</div>}
      </div>
    </div>
  );
}
